import {
  deleteTrackerSubmissionApi,
  getTrackerSubmissionDetailApi,
  getTrackerSubmissionsApi,
  markTrackerSubmissionSeenApi,
  removeTrackerSubmissionMemberApi,
  sendTrackerSubmissionFeedbackApi,
  updateTrackerSubmissionApi,
} from "src/DAL/tracker/trackerSubmission";

/** Canonical sources (backend). Legacy values member/public are still normalized. */
export const SUBMISSION_SOURCES = {
  MEMBER: "gymrite_member",
  PUBLIC: "bms_member",
};

export const SUBMISSION_REVIEW = {
  NONE: "none",
  PENDING: "pending",
  OVERDUE: "overdue",
  REVIEWED: "reviewed",
};

export const SUBMISSION_SOURCE_OPTIONS = [
  { value: "all", label: "All sources" },
  { value: SUBMISSION_SOURCES.MEMBER, label: "GymRite members" },
  { value: SUBMISSION_SOURCES.PUBLIC, label: "Public / BMS user" },
];

export const SUBMISSION_REVIEW_OPTIONS = [
  { value: "all", label: "Any review" },
  { value: SUBMISSION_REVIEW.OVERDUE, label: "Overdue" },
  { value: SUBMISSION_REVIEW.PENDING, label: "Pending" },
  { value: SUBMISSION_REVIEW.REVIEWED, label: "Reviewed" },
  { value: SUBMISSION_REVIEW.NONE, label: "No review" },
];

export const isGymriteMemberSource = (value) =>
  value === SUBMISSION_SOURCES.MEMBER || value === "member";

export const isBmsMemberSource = (value) =>
  value === SUBMISSION_SOURCES.PUBLIC || value === "public";

export const normalizeSubmissionSource = (value) => {
  if (isGymriteMemberSource(value)) return SUBMISSION_SOURCES.MEMBER;
  if (isBmsMemberSource(value)) return SUBMISSION_SOURCES.PUBLIC;
  if (value === SUBMISSION_SOURCES.MEMBER || value === SUBMISSION_SOURCES.PUBLIC) {
    return value;
  }
  return SUBMISSION_SOURCES.PUBLIC;
};

export const getSubmissionSourceLabel = (value) => {
  if (isGymriteMemberSource(value)) return "GymRite member";
  if (isBmsMemberSource(value)) return "BMS user";
  return value || "—";
};

export const getSubmissionReviewLabel = (value) => {
  if (value === SUBMISSION_REVIEW.PENDING) return "Pending";
  if (value === SUBMISSION_REVIEW.OVERDUE) return "Overdue";
  if (value === SUBMISSION_REVIEW.REVIEWED) return "Reviewed";
  return "No review";
};

/** Reasons a member can give when leaving a journey from their portal. */
export const LEAVE_REASON_LABELS = {
  not_right_fit: "Not the right fit",
  injury_health: "Injury or health",
  no_time: "No time",
  other: "Other",
};

export const getLeaveReasonLabel = (value) =>
  LEAVE_REASON_LABELS[value] || "";

export const getInitials = (name = "") => {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const nowIso = () => new Date().toISOString();

export const normalizeSubmission = (raw = {}) => {
  if (!raw || typeof raw !== "object") return null;
  const source = normalizeSubmissionSource(raw.source);
  const firstName = raw.firstName || raw.memberFirstName || "";
  const lastName = raw.lastName || raw.memberLastName || "";
  const memberName =
    raw.memberName ||
    `${firstName} ${lastName}`.trim() ||
    raw.email ||
    "Unknown";

  let answers = {};
  if (raw.answersMap && typeof raw.answersMap === "object") {
    answers = { ...raw.answersMap };
  } else if (Array.isArray(raw.answers)) {
    raw.answers.forEach((entry) => {
      const key = entry?.label || entry?.questionId;
      if (key) answers[key] = entry.value;
    });
  } else if (raw.answers && typeof raw.answers === "object") {
    answers = { ...raw.answers };
  }

  return {
    id: String(
      raw.id ||
        raw._id ||
        `sub_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    ),
    trackerId: String(raw.trackerId || ""),
    trackerName: raw.trackerName || "",
    formId: raw.formId || "",
    formName: raw.formName || "Form",
    source,
    memberId:
      raw.memberId ||
      (source === SUBMISSION_SOURCES.MEMBER ? raw.id : null),
    memberName,
    firstName,
    lastName,
    email: raw.email || "",
    phone: raw.phone || "",
    statusName: raw.statusName || "Submitted",
    statusColor: raw.statusColor || "#0ea5e9",
    tags: Array.isArray(raw.tags)
      ? raw.tags.map((t) => String(t || "").trim()).filter(Boolean)
      : [],
    reviewStatus: [
      SUBMISSION_REVIEW.PENDING,
      SUBMISSION_REVIEW.OVERDUE,
      SUBMISSION_REVIEW.REVIEWED,
      SUBMISSION_REVIEW.NONE,
    ].includes(raw.reviewStatus)
      ? raw.reviewStatus
      : SUBMISSION_REVIEW.NONE,
    reviewDetail: raw.reviewDetail || "",
    reviewedAt: raw.reviewedAt || null,
    leftAt: raw.leftAt || null,
    leftReason: raw.leftReason || "",
    leftNote: raw.leftNote || "",
    leftBy: raw.leftBy || "",
    statusBeforeLeave: raw.statusBeforeLeave || "",
    isNew: Boolean(raw.isNew ?? raw.isUnread),
    coachId: raw.coachId || "",
    coachName: raw.coachName || "",
    coachFeedback: Array.isArray(raw.coachFeedback) ? raw.coachFeedback : [],
    coachNotes: raw.coachNotes || "",
    answers,
    answerSummary: raw.answerSummary || "",
    submittedBy: raw.submittedBy || source,
    submittedAt: raw.submittedAt || nowIso(),
    updatedAt: raw.updatedAt || raw.submittedAt || nowIso(),
  };
};

/** Stable key for unique people across submissions. */
export const getSubmissionPersonKey = (row = {}) =>
  row.memberId || row.email || row.memberName || row.id || "";

export const countUniquePeople = (rows = []) => {
  const keys = new Set();
  (rows || []).forEach((row) => {
    const key = getSubmissionPersonKey(row);
    if (key) keys.add(key);
  });
  return keys.size;
};

export const countReviewsDue = (rows = []) =>
  (rows || []).filter(
    (row) =>
      row.reviewStatus === SUBMISSION_REVIEW.PENDING ||
      row.reviewStatus === SUBMISSION_REVIEW.OVERDUE
  ).length;

/** Load submissions from backend. Returns empty rows if API fails (no demo fallback). */
export const loadSubmissionsFromApi = async (trackerId = null, opts = {}) => {
  try {
    const res = await getTrackerSubmissionsApi(trackerId || null, {
      page: opts.page || 1,
      limit: opts.limit || 500,
      search: opts.search || "",
      source: opts.source || "",
      review: opts.review || "",
      status: opts.status || "",
      tag: opts.tag || "",
      coach: opts.coach || "",
    });
    if (res?.code === 200 && Array.isArray(res.submissions)) {
      return {
        ok: true,
        fromApi: true,
        rows: res.submissions.map(normalizeSubmission).filter(Boolean),
        total: res.total_submissions ?? res.submissions.length,
      };
    }
    return {
      ok: false,
      fromApi: false,
      rows: [],
      message: res?.message || "Failed to load submissions",
    };
  } catch (_e) {
    return {
      ok: false,
      fromApi: false,
      rows: [],
      message: "Failed to load submissions",
    };
  }
};

export const loadSubmissionDetailFromApi = async (id) => {
  try {
    const res = await getTrackerSubmissionDetailApi(id);
    if (res?.code === 200 && res.submission) {
      return {
        ok: true,
        fromApi: true,
        submission: normalizeSubmission(res.submission),
      };
    }
    return {
      ok: false,
      fromApi: false,
      submission: null,
      message: res?.message || "Submission not found",
    };
  } catch (_e) {
    return {
      ok: false,
      fromApi: false,
      submission: null,
      message: "Submission not found",
    };
  }
};

export const deleteSubmissionFromApi = async (id) => {
  try {
    const res = await deleteTrackerSubmissionApi(id);
    if (res?.code === 200) {
      return { ok: true };
    }
    return { ok: false, message: res?.message || "Delete failed" };
  } catch (_e) {
    return { ok: false, message: "Delete failed" };
  }
};

export const markSubmissionSeenFromApi = async (id) => {
  try {
    const res = await markTrackerSubmissionSeenApi(id);
    if (res?.code === 200 && res.submission) {
      return { ok: true, submission: normalizeSubmission(res.submission) };
    }
    return { ok: false };
  } catch (_e) {
    return { ok: false };
  }
};

export const updateSubmissionFromApi = async (id, patch = {}) => {
  try {
    const res = await updateTrackerSubmissionApi(id, patch);
    if (res?.code === 200 && res.submission) {
      return { ok: true, submission: normalizeSubmission(res.submission) };
    }
    return {
      ok: false,
      message: res?.message || "Could not update submission",
    };
  } catch (_e) {
    return { ok: false, message: "Could not update submission" };
  }
};

/** Coach feedback → member. `results` = per-channel { sent, error }. */
export const sendSubmissionFeedbackApi = async (
  id,
  { message = "", sendChat = false, sendEmail = false } = {}
) => {
  try {
    const res = await sendTrackerSubmissionFeedbackApi(id, {
      message,
      sendChat: Boolean(sendChat),
      sendEmail: Boolean(sendEmail),
    });
    if (res?.code === 200) {
      return {
        ok: true,
        message: res.message || "Feedback sent",
        results: res.results || null,
        submission: res.submission ? normalizeSubmission(res.submission) : null,
      };
    }
    return {
      ok: false,
      code: res?.code,
      message: res?.message || "Couldn't send the feedback",
      results: res?.results || null,
    };
  } catch (_e) {
    return { ok: false, message: "Couldn't send the feedback" };
  }
};

/** Coach/admin removes the person from the journey. Optional goodbye channels. */
export const removeSubmissionMemberApi = async (
  id,
  {
    reason = "",
    note = "",
    message = "",
    notifyEmail = false,
    notifyInApp = false,
  } = {}
) => {
  try {
    const res = await removeTrackerSubmissionMemberApi(id, {
      reason,
      note,
      message,
      notifyEmail: Boolean(notifyEmail),
      notifyInApp: Boolean(notifyInApp),
    });
    if (res?.code === 200) {
      return {
        ok: true,
        message: res.message || "Member removed from the journey",
        alreadyRemoved: Boolean(res.alreadyRemoved),
        results: res.results || null,
        submission: res.submission ? normalizeSubmission(res.submission) : null,
      };
    }
    return {
      ok: false,
      code: res?.code,
      message: res?.message || "Couldn't remove the member",
      results: res?.results || null,
    };
  } catch (_e) {
    return { ok: false, message: "Couldn't remove the member" };
  }
};
